import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { LocaleProvider } from './i18n';
import { ApiError, UNAUTHENTICATED_EVENT } from './lib/api-client';
import { queryKeys } from './lib/query-keys';
import { createAppRouter } from './routes/router';
import { ThemedToaster } from './theme/ThemedToaster';
import { ThemeProvider } from './theme/ThemeProvider';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      // Authentication and permission failures are answers, not flaky requests.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? false
          : failureCount < 2,
    },
  },
});

const router = createAppRouter(queryClient);

/**
 * A single place to react to an expired or revoked session: drop the cached
 * user and send the browser back to the login screen with a return path.
 */
window.addEventListener(UNAUTHENTICATED_EVENT, () => {
  queryClient.setQueryData(queryKeys.auth.me, null);
  const { pathname, search } = window.location;
  if (pathname === '/login') return;
  void router.navigate({ to: '/login', search: { next: `${pathname}${search}` } });
});

const container = document.getElementById('root');
if (!container) throw new Error('#root container is missing from index.html');

// Every component this file renders is imported, never declared here. A
// component declared in the entry file would make it a React Fast Refresh
// boundary, and the dev server would then re-run it on an update that reaches
// it — mounting a second React root over the first, which breaks the live page
// until it is reloaded by hand. Without one, such an update reloads the page.
createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <LocaleProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ThemedToaster />
        </QueryClientProvider>
      </LocaleProvider>
    </ThemeProvider>
  </StrictMode>,
);
